import { THINKING_WARN_THRESHOLD_MS } from '@orn/global/assistant/timing';
import toCurrency from '@orn/shared-web/utils/money/toCurrency';

import { sanitizeTranscriptPayload } from '../assistantFeedbackView';

import type {
  AdminGetAssistantThreadDetailQuery,
  AdminListAssistantFeedbackForThreadQuery,
} from '../__generated__/AssistantThreadsScene.gql';

export type MessageRow = AdminGetAssistantThreadDetailQuery['adminGetAssistantThreadDetail']['messages'][number];
export type FeedbackRow = AdminListAssistantFeedbackForThreadQuery['adminListAssistantFeedbackForThread'][number];

export interface IRoleAccent {
  border: string;
  label: string;
  /**
   * Color for the row's `headerPreview` slot — the inline body excerpt
   * (user/assistant prose, tool-call params, system kind). Three tiers
   * by information value: user + assistant get `fg-primary` (brightest —
   * what the operator is actually trying to read); toolResult /
   * system_* get `fg-tertiary` (dim — supporting metadata). The role
   * label itself keeps its accent color regardless, providing
   * row-type recognition independent of preview brightness.
   */
  prose: string;
}

export const ROLE_ACCENT: Record<string, IRoleAccent> = {
  user: { border: 'border-fg-brand-primary', label: 'text-fg-brand-primary', prose: 'text-fg-primary' },
  assistant: { border: 'border-fg-success-primary', label: 'text-fg-success-primary', prose: 'text-fg-primary' },
  toolResult: { border: 'border-fg-warning-primary', label: 'text-fg-warning-primary', prose: 'text-fg-tertiary' },
  system_instruction: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
  system_observation: { border: 'border-border-primary', label: 'text-fg-tertiary', prose: 'text-fg-tertiary' },
};

export const DEFAULT_ROLE_ACCENT: IRoleAccent = {
  border: 'border-border-primary',
  label: 'text-fg-secondary',
  prose: 'text-fg-tertiary',
};

/**
 * Top-level payload keys whose array value renders collapsed by default in the
 * expanded JSON. `content` on a `toolResult` is `[{ type: 'text', text:
 * JSON.stringify(details) }]` — a stringified duplicate of the sibling
 * `details` object (see `jsonResult` in @orn/api), so it's noise an operator
 * almost never wants un-collapsed.
 */
export const COLLAPSED_TOOL_RESULT_KEYS: ReadonlySet<string> = new Set(['content']);

/**
 * Default prose treatment in the rendered JSON: `content` / `text` (the
 * Anthropic SDK message shape's prose keys) render bold bright-white. Cards
 * that want a role-specific color (e.g. user/assistant prose in yellow) pass
 * their own `stringKeyClasses` instead of this.
 */
export const PROMINENT_CONTENT_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['content', 'font-semibold text-fg-primary'],
  ['text', 'font-semibold text-fg-primary'],
]);

/**
 * Message-payload string keys whose value is an opaque, unbreakable blob
 * (no whitespace for `pre-wrap` to break on) — truncated to a short preview
 * so it can't overflow the page width. `thinkingSignature` is the Anthropic
 * extended-thinking integrity token: never human-read, always huge.
 */
export const ELLIPSIZED_STRING_KEYS: ReadonlySet<string> = new Set(['thinkingSignature']);

/** User/assistant prose (`content` string + `text` blocks) renders bold yellow to foreground the conversation in the JSON. */
export const PROSE_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['content', 'font-bold text-yellow-300'],
  ['text', 'font-bold text-yellow-300'],
]);

/**
 * Model-supplied audit params injected into every tool call's arguments
 * (`goal` is a user-facing status fragment, `justification` an internal
 * audit reason). Kept out of the toolResult header summary and surfaced
 * instead in the expanded `toolCall` section. Mirrors `AUDIT_PARAM_KEYS`
 * in @orn/api — duplicated here because the admin surface cannot import
 * from @orn/api.
 */
export const AUDIT_PARAM_KEYS: ReadonlySet<string> = new Set(['goal', 'justification']);

/** Per-key value colors for the expanded toolCall JSON: goal green, justification magenta. */
export const AUDIT_PARAM_COLOR_CLASSES: ReadonlyMap<string, string> = new Map([
  ['goal', 'text-fg-success-primary'],
  ['justification', 'text-fuchsia-400'],
]);

export interface ITurnGroup {
  turnId: string;
  messages: MessageRow[];
  /** ms since epoch of the earliest `startedAt` in the group, or null if every row lacks timing. */
  startedAtMs: number | null;
  /** ms since epoch of the latest `completedAt` in the group, or null if every row lacks timing. */
  completedAtMs: number | null;
  durationMs: number | null;
  roundCount: number;
  toolCallCount: number;
  /** Tokens and cost summed over the turn's rounds; null when no round recorded usage. */
  usage: IRoundUsage | null;
}

/**
 * Partition the loaded messages into ordered turn groups. Each turn's
 * rows are already contiguous in `sequence ASC` (the controller's append
 * contract holds them together), so a single linear pass suffices —
 * boundaries are wherever the `turnId` changes from the previous row.
 *
 * Aggregates (`startedAtMs` / `completedAtMs` / `roundCount` /
 * `toolCallCount` / `usage`) are computed in the same pass so the header
 * can render them without re-walking the group. Rows that pre-date the
 * timing columns (`startedAt`/`completedAt` NULL) contribute to the
 * row count but not to the duration extent, and rows carrying no `usage`
 * block leave the turn's totals alone rather than counting as zero.
 */
export const groupMessagesByTurn = (messages: readonly MessageRow[]): ITurnGroup[] => {
  const groups: ITurnGroup[] = [];
  let current: ITurnGroup | null = null;
  for (const m of messages) {
    if (current === null || current.turnId !== m.turnId) {
      current = {
        turnId: m.turnId,
        messages: [],
        startedAtMs: null,
        completedAtMs: null,
        durationMs: null,
        roundCount: 0,
        toolCallCount: 0,
        usage: null,
      };
      groups.push(current);
    }
    current.messages.push(m);
    if (m.role === 'assistant') {
      current.roundCount += 1;
    }
    if (m.role === 'toolResult') {
      current.toolCallCount += 1;
    }
    const usage = readRoundUsage(m.payload);
    if (usage !== null) {
      current.usage = {
        totalTokens: (current.usage?.totalTokens ?? 0) + usage.totalTokens,
        costUsd: (current.usage?.costUsd ?? 0) + usage.costUsd,
      };
    }
    if (m.startedAt) {
      const ms = new Date(m.startedAt).getTime();
      if (current.startedAtMs === null || ms < current.startedAtMs) {
        current.startedAtMs = ms;
      }
    }
    if (m.completedAt) {
      const ms = new Date(m.completedAt).getTime();
      if (current.completedAtMs === null || ms > current.completedAtMs) {
        current.completedAtMs = ms;
      }
    }
  }
  for (const g of groups) {
    if (g.startedAtMs !== null && g.completedAtMs !== null) {
      g.durationMs = g.completedAtMs - g.startedAtMs;
    }
  }
  return groups;
};

/**
 * `system_instruction` kind that the connection-loss continuation turn leads
 * with (mirrors `SystemInstructionKind.ResumeInterrupted` in @orn/api —
 * duplicated as a literal because the admin surface cannot import from there).
 */
export const RESUME_INTERRUPTED_KIND = 'resume_interrupted';

/**
 * Pretty-print a millisecond duration for the turn header / latency
 * chips. `< 1s` shows as `123ms`; `≥ 1s` shows as `1.23s` so the
 * operator can scan turn lengths at a glance. NULL → `—`. Compact
 * (no space before unit) so per-row chips align cleanly in
 * fixed-width column cells.
 */
export const formatDurationMs = (ms: number | null): string => {
  if (ms === null) {
    return '—';
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
};

/**
 * One renderable chunk inside a turn group. Single `MessageCard`s
 * land as `{ kind: 'single' }`. Contiguous `toolResult` runs of 2+
 * land as `{ kind: 'parallel' }` — by the Anthropic API contract,
 * such a run always pairs to ONE assistant message with N tool_use
 * blocks, and pi-agent-core's `toolExecution: 'parallel'` default
 * means those N tools dispatch concurrently. Single-tool rounds
 * (run length 1) stay as `'single'` since there's nothing to bracket.
 */
export type TurnSegment =
  | { kind: 'single'; message: MessageRow }
  | {
      kind: 'parallel';
      messages: MessageRow[];
      /** ms since epoch — earliest `startedAt` across the parallel siblings (≈ when the assistant dispatched them). */
      startedAtMs: number | null;
      /** Max of each sibling's `latencyMs` — the slowest tool gates when the next round can start, so this is the wall-clock duration of the parallel block. */
      maxLatencyMs: number | null;
    };

export const buildTurnSegments = (messages: readonly MessageRow[]): TurnSegment[] => {
  const segments: TurnSegment[] = [];
  let i = 0;
  while (i < messages.length) {
    if (messages[i].role === 'toolResult') {
      const start = i;
      while (i < messages.length && messages[i].role === 'toolResult') {
        i += 1;
      }
      const slice = messages.slice(start, i);
      if (slice.length >= 2) {
        let startedAtMs: number | null = null;
        let maxLatencyMs: number | null = null;
        for (const m of slice) {
          if (m.startedAt) {
            const ms = new Date(m.startedAt).getTime();
            if (startedAtMs === null || ms < startedAtMs) {
              startedAtMs = ms;
            }
          }
          if (m.latencyMs !== null && m.latencyMs !== undefined) {
            if (maxLatencyMs === null || m.latencyMs > maxLatencyMs) {
              maxLatencyMs = m.latencyMs;
            }
          }
        }
        segments.push({ kind: 'parallel', messages: slice, startedAtMs, maxLatencyMs });
      } else {
        segments.push({ kind: 'single', message: slice[0] });
      }
    } else {
      segments.push({ kind: 'single', message: messages[i] });
      i += 1;
    }
  }
  return segments;
};

/**
 * Format the row's `startedAt` as a Δ-from-turn-start offset (e.g.
 * `+1.23s`). The first row of each turn shows `+0.00 s`. Falls back to
 * `—` when either the row or the turn lacks a `startedAt` — typically
 * legacy rows that pre-date the timing columns.
 */
export const formatTurnDelta = (rowStartedAt: Date | string | null, turnStartedAtMs: number | null): string => {
  if (rowStartedAt === null || turnStartedAtMs === null) {
    return '—';
  }
  const deltaMs = new Date(rowStartedAt).getTime() - turnStartedAtMs;
  if (deltaMs < 0) {
    return '—';
  }
  if (deltaMs < 1000) {
    return `+${deltaMs}ms`;
  }
  return `+${(deltaMs / 1000).toFixed(2)}s`;
};

/**
 * Walk every assistant row in `messages` and index every `toolCall`
 * content block by its `id`, returning `Map<toolCallId, arguments>` so
 * a paired `toolResult` row's params can be looked up O(1). pi-agent-
 * core normalizes Anthropic's raw `tool_use` (`input`) and OpenAI's
 * `function_call` (`arguments`) into one persisted shape on
 * `AssistantMessage.content[]` — `{ type: 'toolCall', id, name,
 * arguments }` — and pairs the toolResult by `toolCallId` ↔
 * `toolCall.id`. Single pass per turn at render time, no extra fetch.
 */
export const buildToolCallArgsIndex = (messages: readonly MessageRow[]): Map<string, unknown> => {
  const index = new Map<string, unknown>();
  for (const m of messages) {
    if (m.role !== 'assistant' || m.payload === null || typeof m.payload !== 'object') {
      continue;
    }
    const content = (m.payload as Record<string, unknown>).content;
    if (!Array.isArray(content)) {
      continue;
    }
    for (const block of content) {
      if (block === null || typeof block !== 'object') {
        continue;
      }
      const b = block as Record<string, unknown>;
      if (b.type === 'toolCall' && typeof b.id === 'string') {
        index.set(b.id, b.arguments);
      }
    }
  }
  return index;
};

/**
 * Render a tool call's `input` object as `(key: value, key: value, ...)`
 * for the toolResult card's header preview. Values are rendered as a
 * short scalar form so the line stays scannable; the full structure is
 * still in the JSON body on expand. Keys in `excludeKeys` are dropped
 * from the summary (the audit params `goal` / `justification` are surfaced
 * separately in the expanded toolCall section, not inline). Returns `''`
 * for an empty / missing input — or one whose only keys are excluded — so
 * the caller can fall through to the no-preview case.
 */
export const formatToolParams = (input: unknown, excludeKeys?: ReadonlySet<string>): string => {
  if (input === null || typeof input !== 'object') {
    return '';
  }
  const entries = Object.entries(input as Record<string, unknown>).filter(([k]) => !(excludeKeys?.has(k) ?? false));
  if (entries.length === 0) {
    return '';
  }
  const parts = entries.map(([k, v]) => `${k}: ${formatParamValue(v)}`);
  return `(${parts.join(', ')})`;
};

const MAX_PARAM_STRING_LEN = 40;
const formatParamValue = (v: unknown): string => {
  if (v === null) {
    return 'null';
  }
  if (typeof v === 'string') {
    return v.length > MAX_PARAM_STRING_LEN ? `"${v.slice(0, MAX_PARAM_STRING_LEN)}…"` : `"${v}"`;
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  if (Array.isArray(v)) {
    return `[${v.length}]`;
  }
  if (typeof v === 'object') {
    return `{${Object.keys(v as Record<string, unknown>).length}}`;
  }
  return typeof v;
};

/**
 * Extract a `toolResult` row's `toolCallId` from its GraphQL JSON payload.
 * Defensive — a producer drift that drops the field shouldn't crash the
 * renderer.
 */
export const readToolCallId = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const id = (payload as Record<string, unknown>).toolCallId;
  return typeof id === 'string' ? id : null;
};

/**
 * Narrow the `toolResult` payload shape from the `unknown` GraphQL JSON
 * scalar so the collapsed header can surface `toolName` + `isError`
 * without rendering the (often noisy) `details` body. Defensive on every
 * field — a future producer drift shouldn't crash the renderer.
 */
export const readToolResultPayload = (payload: unknown): { toolName: string | null; isError: boolean } => {
  if (payload === null || typeof payload !== 'object') {
    return { toolName: null, isError: false };
  }
  const obj = payload as Record<string, unknown>;
  const toolName = typeof obj.toolName === 'string' ? obj.toolName : null;
  const isError = obj.isError === true;
  return { toolName, isError };
};

/**
 * One assistant round's token + cost usage, as the framework recorded it on the
 * message payload. `totalTokens` is the provider's own total; `costUsd` is what
 * that single round billed, priced at call time.
 */
export interface IRoundUsage {
  totalTokens: number;
  costUsd: number;
}

const readNumber = (obj: Record<string, unknown>, key: string): number | null => {
  const value = obj[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/** Cost precision for a single round: enough to show sub-cent model spend. */
const COST_MAX_FRACTION_DIGITS = 4;

/** USD spend, at a precision that still distinguishes two cheap rounds. */
export const formatCostUsd = (usd: number): string => toCurrency(usd, { maxFractionDigits: COST_MAX_FRACTION_DIGITS });

/**
 * Read one round's usage off its GraphQL JSON payload.
 *
 * Null on every row that carries no `usage` block — user, toolResult and
 * synthetic rows, and assistant rows persisted before the framework reported it.
 * Defensive on every field, like the other payload readers here: a producer
 * drift shouldn't crash the transcript.
 */
export const readRoundUsage = (payload: unknown): IRoundUsage | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const usage = (payload as Record<string, unknown>).usage;
  if (usage === null || typeof usage !== 'object') {
    return null;
  }
  const usageObj = usage as Record<string, unknown>;
  const totalTokens = readNumber(usageObj, 'totalTokens');
  const cost = usageObj.cost;
  const costUsd =
    cost !== null && typeof cost === 'object' ? readNumber(cost as Record<string, unknown>, 'total') : null;
  if (totalTokens === null && costUsd === null) {
    return null;
  }
  return { totalTokens: totalTokens ?? 0, costUsd: costUsd ?? 0 };
};

/**
 * Tool names of the alert aggregate's create + notify calls (mirror the
 * @orn/api registry — duplicated here because the admin surface cannot
 * import from it).
 */
export const ALERT_CREATE_TOOL_NAME = 'alert_create';
export const ALERT_NOTIFY_TOOL_NAME = 'alert_notify';

/** The notify result `status` meaning the hand-off was actually made (mirrors the tool's result contract). */
const ALERT_NOTIFY_DISPATCHED_STATUS = 'dispatched';

/** Channel a dispatched notify covered an alert on; `unknown` when the call's args aren't among the loaded rows. */
export type AlertNotifyChannel = 'sms' | 'email' | 'unknown';

/** The notify result's payload proper — `details.data` under the tool's `{ data, metadata }` envelope. */
const readNotifyResultData = (payload: unknown): Record<string, unknown> | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const details = (payload as Record<string, unknown>).details;
  if (details === null || typeof details !== 'object') {
    return null;
  }
  const data = (details as Record<string, unknown>).data;
  if (data === null || typeof data !== 'object') {
    return null;
  }
  return data as Record<string, unknown>;
};

const readNotifyChannel = (args: unknown): AlertNotifyChannel => {
  if (args !== null && typeof args === 'object') {
    const channel = (args as Record<string, unknown>).channel;
    if (channel === 'sms' || channel === 'email') {
      return channel;
    }
  }
  return 'unknown';
};

/**
 * alertId → channels a dispatched `alert_notify` among `messages` covered it
 * on. Joins each notify result's `notifiedAlertIds` (the open subset the
 * message actually covers — never the raw request) with the call's `channel`
 * argument via {@link buildToolCallArgsIndex}; a notify whose originating
 * call isn't among the loaded rows still counts, as channel `unknown`.
 * Channels dedupe per alert, so re-notifying on a later pass adds nothing.
 * Skipped and failed notifies contribute nothing.
 *
 * Scope is exactly the rows passed in: a notify on another thread, or in a
 * not-yet-loaded page, is invisible — an absent entry means "not notified
 * *here*", never "not notified". And `dispatched` is hand-off-level truth
 * (the tool's own contract), so the index inherits that, not delivery.
 */
export const buildAlertNotificationIndex = (
  messages: readonly MessageRow[]
): Map<string, readonly AlertNotifyChannel[]> => {
  const argsByToolCallId = buildToolCallArgsIndex(messages);
  const channelsByAlertId = new Map<string, AlertNotifyChannel[]>();
  for (const m of messages) {
    if (m.role !== 'toolResult') {
      continue;
    }
    const { toolName, isError } = readToolResultPayload(m.payload);
    if (toolName !== ALERT_NOTIFY_TOOL_NAME || isError) {
      continue;
    }
    const data = readNotifyResultData(m.payload);
    if (data === null || data.status !== ALERT_NOTIFY_DISPATCHED_STATUS || !Array.isArray(data.notifiedAlertIds)) {
      continue;
    }
    const toolCallId = readToolCallId(m.payload);
    const channel = readNotifyChannel(toolCallId === null ? undefined : argsByToolCallId.get(toolCallId));
    for (const alertId of data.notifiedAlertIds) {
      if (typeof alertId !== 'string') {
        continue;
      }
      const channels = channelsByAlertId.get(alertId);
      if (channels === undefined) {
        channelsByAlertId.set(alertId, [channel]);
      } else if (!channels.includes(channel)) {
        channels.push(channel);
      }
    }
  }
  return channelsByAlertId;
};

/**
 * The created alert's id from an `alert_create` toolResult payload. Compiled
 * ops project their result straight into `details` (no `data` envelope), and
 * `id` is present when the model selected it — which it effectively always
 * does, since the id is what every later call acts on. Null for other tools,
 * failed calls, and id-less projections.
 */
export const readCreatedAlertId = (payload: unknown): string | null => {
  const { toolName, isError } = readToolResultPayload(payload);
  if (toolName !== ALERT_CREATE_TOOL_NAME || isError) {
    return null;
  }
  const details = (payload as Record<string, unknown>).details;
  if (details === null || typeof details !== 'object') {
    return null;
  }
  const id = (details as Record<string, unknown>).id;
  return typeof id === 'string' ? id : null;
};

/**
 * Pull the `kind` discriminator out of a `system_observation` or
 * `system_instruction` payload (`'attachment'`, `'session_bootstrap'`,
 * etc.). Both payload families use the same `kind` field and the enum
 * is intentionally open to grow — keep the read defensive so a newly
 * added kind that mops-web hasn't been rebuilt against still renders
 * cleanly.
 */
export const readPayloadKind = (payload: unknown): string | null => {
  if (payload === null || typeof payload !== 'object') {
    return null;
  }
  const kind = (payload as Record<string, unknown>).kind;
  return typeof kind === 'string' ? kind : null;
};

/**
 * True when this turn is the server's connection-loss continuation, run on the
 * next connect after the WebSocket dropped mid-turn — its first row is a
 * `system_instruction` of kind `resume_interrupted`. The transcript renders an
 * interstitial before such a turn to mark where the connection dropped and the
 * assistant picked back up. A zero-output-bootstrap recovery instead re-runs
 * the `session_bootstrap` directive, which is indistinguishable from a
 * first-connect greeting in the persisted transcript, so it is not flagged.
 */
export const isResumeInterruptedTurn = (group: ITurnGroup): boolean => {
  const first = group.messages[0];
  return first?.role === 'system_instruction' && readPayloadKind(first.payload) === RESUME_INTERRUPTED_KIND;
};

export interface IObservationDocument {
  id: string;
  name: string | null;
}

/**
 * Pull the attachment `documents` array out of a `system_observation`
 * payload of kind `attachment` — each entry is the `{ id, name }` of an
 * uploaded document the inspector turns into a download link. Defensive
 * on every field: a non-object payload, a missing/non-array `documents`,
 * or an entry without a string `id` is dropped rather than crashing the
 * renderer. A non-string `name` is coerced to `null`.
 */
export const readObservationDocuments = (payload: unknown): IObservationDocument[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const documents = (payload as Record<string, unknown>).documents;
  if (!Array.isArray(documents)) {
    return [];
  }
  const out: IObservationDocument[] = [];
  for (const entry of documents) {
    if (entry !== null && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      if (typeof e.id === 'string') {
        out.push({ id: e.id, name: typeof e.name === 'string' ? e.name : null });
      }
    }
  }
  return out;
};

/** The tool the assistant speaks through on SMS and email. */
const SEND_REPLY_TOOL = 'send_reply';

/**
 * Pull the assistant's prose out of a user or assistant message's `content`
 * array. The Anthropic SDK message shape interleaves `text` and `tool_use`
 * (or, on user rows, `tool_result`) blocks; the prose is what an operator
 * scanning a transcript actually wants to read, so we lift it above the
 * collapsed JSON. Defensive — non-array content (legacy plain-string payloads,
 * or future shape drift) falls back gracefully.
 *
 * A `send_reply` argument counts as prose: on SMS and email it is what the
 * household read, and without it the row that carries the reply renders as
 * "Thinking…" with the message truncated inside a collapsed tool call.
 */
export const extractTextBlocks = (payload: unknown): string[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const content = (payload as Record<string, unknown>).content;
  if (typeof content === 'string') {
    return content.trim().length > 0 ? [content] : [];
  }
  if (!Array.isArray(content)) {
    return [];
  }
  const out: string[] = [];
  for (const block of content) {
    if (block !== null && typeof block === 'object') {
      const b = block as Record<string, unknown>;
      if (b.type === 'text' && typeof b.text === 'string' && b.text.trim().length > 0) {
        out.push(b.text);
      }
      const replyMessage = readSendReplyMessage(b);
      if (replyMessage !== null) {
        out.push(replyMessage);
      }
    }
  }
  return out;
};

/**
 * The message of a `send_reply` call, or null for any other block.
 *
 * Reads the persisted shape, `{ type: 'toolCall', arguments }` — the same one
 * {@link buildToolCallArgsIndex} indexes. The provider's raw `tool_use` /
 * `input` spelling is normalized away before a row is written, so matching on
 * it would recognize nothing in production.
 */
const readSendReplyMessage = (block: Record<string, unknown>): string | null => {
  if (block.type !== 'toolCall' || block.name !== SEND_REPLY_TOOL) {
    return null;
  }
  const args = block.arguments;
  if (args === null || typeof args !== 'object') {
    return null;
  }
  const message = (args as Record<string, unknown>).message;
  return typeof message === 'string' && message.trim().length > 0 ? message : null;
};

/**
 * True when this row is an assistant round that spent longer than the shared
 * threshold inside `thinking` blocks. Mirrors the backend predicate in
 * `collectSlowThinkingRounds` (same `@orn/global` bar) so the viewer flags
 * exactly the rounds that trip the Sentry warning.
 */
export const isSlowThinkingRound = (message: MessageRow): boolean =>
  message.role === 'assistant' && message.thinkingMs != null && message.thinkingMs > THINKING_WARN_THRESHOLD_MS;

/** True when any round in the turn is a slow-thinking round (drives the turn-header badge). */
export const groupHasSlowThinkingRound = (group: ITurnGroup): boolean => group.messages.some(isSlowThinkingRound);

/**
 * The `system_instruction` kind recording that a turn was about to stop without
 * sending, and was asked for the answer.
 */
export const UNSENT_ANSWER_NUDGE_KIND = 'unsent_answer_nudge';

/**
 * True when this row is that record.
 *
 * The nudge reaches the loop as an imperative but persists as a past-tense
 * note, so the reply after it has a visible cause without a stale command
 * replaying on later turns. Whether the model complied is read from the rows
 * that follow: a later `send_reply` means the turn recovered, and no further
 * send means the nudge was ignored and the household got silence.
 */

export const isNudgedUnsentAnswerRound = (message: MessageRow): boolean =>
  message.role === 'system_instruction' && readPayloadKind(message.payload) === UNSENT_ANSWER_NUDGE_KIND;

/** True when any round in the turn drew a nudge (drives the turn-header badge). */
export const groupHasNudgedUnsentAnswer = (group: ITurnGroup): boolean =>
  group.messages.some(isNudgedUnsentAnswerRound);

/**
 * True when a nudged turn never got an answer to the household — the shape
 * worth escalating, as opposed to a nudge the model answered.
 *
 * Reads the rows after the last nudged round, and requires a send that
 * *landed*: the call is correlated with its `toolResult` and an errored one
 * does not count. The sink rejects an empty message, a lost lease, and an
 * opted-out number, so a turn that ended on one left the household with
 * nothing — which is the same reading the orchestrator takes when it decides
 * what to report, and the two surfaces must not disagree about whether a
 * household was reached.
 */
export const groupNudgeWentUnanswered = (group: ITurnGroup): boolean => {
  const lastNudged = group.messages.findLastIndex(isNudgedUnsentAnswerRound);
  if (lastNudged === -1) {
    return false;
  }
  const after = group.messages.slice(lastNudged + 1);
  const sendCallIds = new Set(after.flatMap((m) => (m.role === 'assistant' ? sendReplyCallIds(m.payload) : [])));
  if (sendCallIds.size === 0) {
    return true;
  }
  return !after.some((m) => m.role === 'toolResult' && isLandedSendResult(m, sendCallIds));
};

/** Whether a `toolResult` row is the successful result of one of `sendCallIds`. */
const isLandedSendResult = (message: MessageRow, sendCallIds: ReadonlySet<string>): boolean => {
  const toolCallId = readToolCallId(message.payload);
  return toolCallId !== null && sendCallIds.has(toolCallId) && !readToolResultPayload(message.payload).isError;
};

/** Ids of the `send_reply` calls an assistant payload carries. */
const sendReplyCallIds = (payload: unknown): string[] => {
  if (payload === null || typeof payload !== 'object') {
    return [];
  }
  const content = (payload as { content?: unknown }).content;
  if (!Array.isArray(content)) {
    return [];
  }
  const ids: string[] = [];
  for (const block of content) {
    if (block === null || typeof block !== 'object') {
      continue;
    }
    const b = block as Record<string, unknown>;
    if (readSendReplyMessage(b) !== null && typeof b.id === 'string') {
      ids.push(b.id);
    }
  }
  return ids;
};

/**
 * Serialize one turn's full transcript as a fenced JSON block for the clipboard
 * — every row in append order, payloads run through `sanitizeTranscriptPayload`
 * (drops the duplicated `toolResult.content` and the opaque `thinkingSignature`)
 * so the copied text is the conversation, not multi-KB integrity tokens. The
 * per-row timing (`thinkingMs` / `latencyMs`) rides along so a pasted transcript
 * carries the same debugging signal the inspector shows.
 */
export const buildTurnClipboardText = (group: ITurnGroup): string => {
  const turn = {
    turnId: group.turnId,
    messages: group.messages.map((m) => ({
      sequence: m.sequence,
      role: m.role,
      modelId: m.modelId,
      thinkingMs: m.thinkingMs,
      latencyMs: m.latencyMs,
      payload: sanitizeTranscriptPayload(m.role, m.payload),
    })),
  };
  return ['```json', JSON.stringify(turn, null, 2), '```'].join('\n');
};
